import { create } from 'zustand';
import { useKeycloak } from '@react-keycloak/web';
import { useCallback } from 'react';
import { 
  EscalationMatrixRecord, 
  EscalationMatrixResponse, 
  EscalationMatrixLevel, 
  EscalationMatrixRole,
  getEscalationMatrix,
  getEscalationMatrixLevels,
  getEscalationMatrixRoles,
  updateEscalationMatrixLevel
} from '../api/escalationMatrix';

interface EscalationMatrixStore {
  records: { [projectId: string]: EscalationMatrixRecord[] };
  levels: { [projectId: string]: EscalationMatrixLevel[] };
  roles: EscalationMatrixRole[];
  pagination: {
    page: number;
    size: number;
    total: number;
  };
  loading: boolean;
  error: string | null;
  setRecords: (projectId: string, data: EscalationMatrixResponse) => void;
  setLevels: (projectId: string, levels: EscalationMatrixLevel[]) => void;
  setRoles: (roles: EscalationMatrixRole[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useEscalationMatrixStore = create<EscalationMatrixStore>((set) => ({
  records: {},
  levels: {},
  roles: [],
  pagination: {
    page: 1,
    size: 100,
    total: 0
  },
  loading: false,
  error: null,
  setRecords: (projectId: string, data: EscalationMatrixResponse) => {
    set((state) => ({
      records: {
        ...state.records,
        [projectId]: data.results
      },
      pagination: {
        page: data.page,
        size: data.size,
        total: data.total
      }
    }));
  },
  setLevels: (projectId: string, levels: EscalationMatrixLevel[]) => {
    set((state) => ({
      levels: {
        ...state.levels,
        [projectId]: levels
      }
    }));
  },
  setRoles: (roles: EscalationMatrixRole[]) => {
    set({ roles });
  },
  setLoading: (loading: boolean) => {
    set({ loading });
  },
  setError: (error: string | null) => {
    set({ error });
  }
}));

export const useEscalationMatrixPage = (projectId: string) => {
  const { keycloak } = useKeycloak();
  const store = useEscalationMatrixStore();

  const loadEscalationMatrix = useCallback(async (page: number = 1, size: number = 100) => {
    if (!keycloak.token || !projectId) return;

    store.setLoading(true);
    store.setError(null);

    try {
      const data = await getEscalationMatrix(keycloak.token, projectId, page, size);
      store.setRecords(projectId, data);
    } catch (error: any) {
      store.setError(error.message || 'Failed to load escalation matrix');
    } finally {
      store.setLoading(false);
    }
  }, [keycloak.token, projectId, store]);

  const loadEscalationMatrixLevels = useCallback(async () => {
    if (!keycloak.token || !projectId) return;

    try {
      const levels = await getEscalationMatrixLevels(keycloak.token, projectId);
      store.setLevels(projectId, levels);
    } catch (error: any) {
      console.error('Failed to load escalation matrix levels:', error);
    }
  }, [keycloak.token, projectId, store]);

  const loadEscalationMatrixRoles = useCallback(async () => {
    if (!keycloak.token) return;

    try {
      const roles = await getEscalationMatrixRoles(keycloak.token);
      store.setRoles(roles);
    } catch (error: any) {
      console.error('Failed to load escalation matrix roles:', error);
    }
  }, [keycloak.token, store]);

  const updateEscalationLevel = useCallback(async (levelId: string, updates: Partial<EscalationMatrixLevel>) => {
    if (!keycloak.token || !projectId) return;

    try {
      // Find current level to get all its fields
      const currentLevels = store.levels[projectId] || [];
      const currentLevel = currentLevels.find(level => level.id === levelId);
      
      if (!currentLevel) {
        throw new Error('Level not found');
      }

      // Prepare full data for update
      const fullUpdateData = {
        ...currentLevel,
        ...updates,
        project_id: projectId // Ensure project_id is included
      };

      console.log('Updating level with full data:', fullUpdateData);

      const updatedLevel = await updateEscalationMatrixLevel(keycloak.token, levelId, fullUpdateData);
      
      // Update the local store
      const updatedLevels = currentLevels.map(level => 
        level.id === levelId ? updatedLevel : level
      );
      store.setLevels(projectId, updatedLevels);
      
      return updatedLevel;
    } catch (error: any) {
      console.error('Failed to update escalation level:', error);
      throw error;
    }
  }, [keycloak.token, projectId, store]);

  return {
    loadEscalationMatrix,
    loadEscalationMatrixLevels,
    loadEscalationMatrixRoles,
    updateEscalationLevel
  };
}; 